import React from 'react';
import { Alert, Button, Empty, Spin } from 'antd';
import { useApp } from '../context/AppContext';
import { Role } from '../constants/roles';
import { useSupportApi } from '../hooks/useSupportApi';
import { useResource } from '../hooks/useResource';
import SupportConsolePage from './SupportConsolePage';
import MySupportPage from './MySupportPage';

/**
 * Single SUPPORT nav destination. Which screen renders is decided by the SERVER's effective
 * roles (`GET /me` -> `effectiveStaffRole`, the same resolution the ticket routes gate on), never
 * by a local pick from the token's roles — a multi-role token (e.g. lead_advisor + platform_admin)
 * is treated as staff by the API, so the UI must agree or it would show cross-tenant rows under
 * "your own tickets".
 *   effectiveStaffRole set (platform_admin / support_agent) -> cross-tenant console
 *   otherwise Lead Advisor / Company SPOC                   -> raise + follow own tickets
 * Anyone else gets an explicit message, not a blank pane. Re-evaluated on every mount, never cached.
 */
const SupportPage = () => {
    const { user } = useApp();
    const api = useSupportApi();
    const me = useResource(() => api.me(), [api]);

    if (me.status === 'loading' || me.status === 'idle') return <Spin style={{ margin: 24 }} />;
    if (me.status === 'error') {
        return (
            <Alert
                type="error"
                showIcon
                style={{ margin: 24 }}
                message="Could not determine your support access"
                description={me.error.message}
                action={<Button onClick={me.reload}>Retry</Button>}
            />
        );
    }
    if (me.data.effectiveStaffRole) return <SupportConsolePage myUserId={me.data.dbUserId} />;
    if (user?.role === Role.LEAD_ADVISOR || user?.role === Role.COMPANY_SPOC) return <MySupportPage />;
    return <Empty style={{ marginTop: 80 }} description="Support tickets are not available for your role." />;
};

export default SupportPage;
